/**
 * Brand Design-Life Metadata
 * 
 * Provides manufacturer-based typical design life estimates for boiler brands.
 * This reflects engineering reality: not all boilers are designed to age gracefully.
 * Some brands are built to hit a price point, not a lifespan.
 * 
 * Past design life doesn't mean "might break" - it means:
 * - Seals harden
 * - Aluminium corrodes
 * - Heat exchangers thin
 * - Control boards drift
 * - Efficiency collapses even if it "still works"
 */

import type { BrandDesignLifeBand } from './types';

/**
 * Brand to design-life band mapping
 * Based on typical engineering lifespan and manufacturer design intent
 */
export const BRAND_DESIGN_LIFE_MAP: Record<string, BrandDesignLifeBand> = {
  // SHORT (8-12 years) - Budget-focused brands
  'ariston': 'SHORT',
  'biasi': 'SHORT',
  'ferroli': 'SHORT',
  'ravenheat': 'SHORT',
  'glow-worm': 'SHORT',
  
  // STANDARD (12-15 years) - Mainstream brands
  'ideal': 'STANDARD',
  'vokera': 'STANDARD',
  'alpha': 'STANDARD',
  'potterton': 'STANDARD',
  'baxi': 'STANDARD',
  'main': 'STANDARD',
  'intergas': 'STANDARD',
  
  // LONG (15-20+ years) - Premium engineering brands
  'worcester': 'LONG',
  'worcester bosch': 'LONG',
  'vaillant': 'LONG',
  'viessmann': 'LONG',
  'atag': 'LONG',
  'valliant': 'LONG',  // Common misspelling
};

/**
 * Design life ranges for each band
 */
export const DESIGN_LIFE_RANGES: Record<BrandDesignLifeBand, { min: number; max: number }> = {
  'SHORT': { min: 8, max: 12 },
  'STANDARD': { min: 12, max: 15 },
  'LONG': { min: 15, max: 20 },
  'UNKNOWN': { min: 12, max: 15 },  // Default to STANDARD range
};

/**
 * Lookup design-life band for a boiler brand
 * 
 * @param boilerMake - Boiler manufacturer/brand name
 * @returns Design life band classification
 */
export function getDesignLifeBand(boilerMake: string | undefined): BrandDesignLifeBand {
  if (!boilerMake) {
    return 'UNKNOWN';
  }
  
  // Normalize brand name: lowercase, trim whitespace
  const normalized = boilerMake.toLowerCase().trim();
  
  // Direct lookup
  if (normalized in BRAND_DESIGN_LIFE_MAP) {
    return BRAND_DESIGN_LIFE_MAP[normalized];
  }
  
  // Partial match using word boundaries to avoid false matches
  // Only match if the brand name appears as a complete word or at start of input
  for (const [brand, band] of Object.entries(BRAND_DESIGN_LIFE_MAP)) {
    // Check if input starts with brand name (followed by space or end)
    if (normalized.startsWith(brand + ' ') || normalized === brand) {
      return band;
    }
    // Check if brand appears as complete word in middle (space before and after)
    if (normalized.includes(' ' + brand + ' ')) {
      return band;
    }
  }
  
  return 'UNKNOWN';
}

/**
 * Get design life range in years for a brand
 * 
 * @param boilerMake - Boiler manufacturer/brand name
 * @returns Design life range { min, max } in years
 */
export function getDesignLifeRange(boilerMake: string | undefined): { min: number; max: number } {
  const band = getDesignLifeBand(boilerMake);
  return DESIGN_LIFE_RANGES[band];
}

/**
 * Check if a boiler is past its typical design life
 * 
 * @param boilerMake - Boiler manufacturer/brand name
 * @param boilerAge - Age of boiler in years
 * @returns true if past design life, false otherwise
 */
export function isPastDesignLife(
  boilerMake: string | undefined,
  boilerAge: number | undefined
): boolean {
  // Explicitly check for null/undefined to handle age 0 correctly
  if (boilerAge == null || boilerAge < 0 || !boilerMake) {
    return false;
  }
  
  const designLife = getDesignLifeRange(boilerMake);
  return boilerAge > designLife.max;
}

/**
 * Generate user-facing warning message for boilers past design life
 * 
 * @param boilerAge - Age of boiler in years
 * @param boilerMake - Boiler manufacturer/brand name
 * @returns Warning message or undefined if not past design life
 */
export function getDesignLifeWarning(
  boilerAge: number | undefined,
  boilerMake: string | undefined
): string | undefined {
  if (!isPastDesignLife(boilerMake, boilerAge)) {
    return undefined;
  }
  
  return "This boiler is operating beyond its typical design life. Continued operation is possible, but reliability, efficiency, and repair costs become increasingly unpredictable.";
}

/**
 * Normalize boiler age from various input formats
 * 
 * @param installYear - Year boiler was installed (e.g., 2015)
 * @param ageYears - Direct age in years
 * @returns Normalized age in years, or undefined if cannot determine
 */
export function normalizeBoilerAge(
  installYear: number | undefined,
  ageYears: number | undefined
): number | undefined {
  // Direct age takes priority
  if (ageYears !== undefined && ageYears >= 0) {
    return Math.round(ageYears);
  }
  
  // Calculate from install year
  if (installYear !== undefined && installYear > 1900) {
    const currentYear = new Date().getFullYear();
    if (installYear <= currentYear) {
      return currentYear - installYear;
    }
  }
  
  return undefined;
}
